import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models
// ============================================================

export interface F90F1 {
  f90f1_01: string;  // 異動單號 (FK)
  f90f1_02: number;  // 序號
  f90f1_11: string;  // 異動事由
  f90f1_12: string;  // 說明
  f90f1_91: string;  // 建立人員
  f90f1_92: string | null;
  f90f1_93: string;
}

export interface F9441 {
  f9441_01: string;   // 異動單號 (FK)
  f9441_02: number;   // 序號
  f9441_10: string;   // 員工代號
  f9441_11: string;   // 薪資代號
  f9441_11a: string;  // 薪資名稱
  f9441_12: number;   // 調整後金額
  f9441_20: string;   // 月份
  f9441_21: number;   // 原有金額
}

export interface F90F {
  f90f_01: string;        // 異動單號 (PK)
  f90f_11: string;        // 員工代號
  f90f_12: string | null; // 入單日期
  f90f_13: string;        // 說明
  f90f_15: string;        // 入單人員
  f90f_21: string;        // 原任部門
  f90f_22: string;        // 原任職稱
  f90f_23: string;        // 原任職等
  f90f_24: string | null; // 到職日
  f90f_31: string;        // 新任部門
  f90f_32: string;        // 新任職稱
  f90f_33: string;        // 新任職等
  f90f_38: string | null; // 擬生效日
  f90f_39: string | null; // 實際生效日
  f90f_40: string | null; // 最後列印日期
  f90f_41: number;        // 異動事由筆數
  f90f_42: number;        // 薪資異動筆數
  f90f_43: boolean;       // 知會原任主管
  f90f_67: string;        // 人事主管代號
  f90f_68: string | null; // 人事主管會簽日期
  f90f_69: string;        // 人事主管核准說明
  f90f_71: string;        // 副總核准 Y/N
  f90f_72: string;        // 副總代號
  f90f_73: string | null; // 副總核准日期
  f90f_74: string;        // 副總核准說明
  f90f_76: string;        // 總經理核准 Y/N
  f90f_77: string;        // 總經理代號
  f90f_78: string | null; // 總經理核准日期
  f90f_79: string;        // 總經理核准說明
  f90f_80: string;        // 版本標記
  f90f_81: string;        // 已存檔
  f90f_91: string;        // 建立人員
  f90f_92: string | null;
  f90f_93: string;        // 修改人員
  details?: F90F1[];
  salary_details?: F9441[];
}

export interface DetailRequest {
  f90f1_11: string; // 異動事由
  f90f1_12: string; // 說明
}

export interface CreateRequest {
  f90f_11: string;           // 員工代號
  f90f_13: string;           // 說明
  f90f_21: string;           // 原任部門
  f90f_22: string;           // 原任職稱
  f90f_23: string;           // 原任職等
  f90f_24: string | null;    // 到職日
  f90f_31: string;           // 新任部門
  f90f_32: string;           // 新任職稱
  f90f_33: string;           // 新任職等
  f90f_38: string | null;    // 擬生效日
  f90f_43: boolean;          // 知會原任主管
  details: DetailRequest[];
}

export interface UpdateRequest {
  f90f_11: string;
  f90f_13: string;
  f90f_21: string;
  f90f_22: string;
  f90f_23: string;
  f90f_24: string | null;
  f90f_31: string;
  f90f_32: string;
  f90f_33: string;
  f90f_38: string | null;
  f90f_43: boolean;
  details: DetailRequest[];
}

export interface ListResponse {
  data: F90F[];
  total: number;
  page: number;
  size: number;
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface ApprovalResponse {
  id: string;
  action: string;
  old_status: string;
  new_status: string;
  message: string;
}

export interface ApprovalHistory {
  id: number;
  document_no: string;
  action: string;
  actor_id: string;
  actor_name: string;
  comment: string;
  old_status: string;
  new_status: string;
  created_at: string;
}

// ============================================================
// API methods
// ============================================================

export const pw9204f1Api = {
  async list(page = 1, size = 100, keyword?: string): Promise<ListResponse> {
    const params: Record<string, string | number> = { page, size };
    if (keyword) params.keyword = keyword;
    const res = await apiClient.get('/api/pw9204f1', { params });
    return res.data;
  },

  async get(id: string): Promise<F90F> {
    const res = await apiClient.get(`/api/pw9204f1/${encodeURIComponent(id)}`);
    return res.data;
  },

  async create(data: CreateRequest): Promise<F90F> {
    const res = await apiClient.post('/api/pw9204f1', data);
    return res.data;
  },

  async update(id: string, data: UpdateRequest): Promise<F90F> {
    const res = await apiClient.put(`/api/pw9204f1/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/pw9204f1/${encodeURIComponent(id)}`);
  },

  // Approval workflow
  async submitForApproval(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/pw9204f1/${encodeURIComponent(id)}/submit`, { comment: comment || '' });
    return res.data;
  },

  async approve(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/pw9204f1/${encodeURIComponent(id)}/approve`, { comment: comment || '' });
    return res.data;
  },

  async reject(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/pw9204f1/${encodeURIComponent(id)}/reject`, { comment: comment || '' });
    return res.data;
  },

  async writeoff(id: string, comment?: string): Promise<ApprovalResponse> {
    const res = await apiClient.post(`/api/pw9204f1/${encodeURIComponent(id)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  async getApprovalHistory(id: string): Promise<ApprovalHistory[]> {
    const res = await apiClient.get(`/api/pw9204f1/${encodeURIComponent(id)}/approval-history`);
    return res.data.data || [];
  },
};
